import { useCallback, useEffect, useState } from "react";
import {
  Row,
  Col,
  Form,
  Button,
  Input,
  Select,
  Checkbox,
  DatePicker,
  Spin,
  Empty,
  message,
  Popover
} from 'antd';
import { getDepartments, getDoctors, getServices } from "../../services/general";
import { consultationService } from "../../services/medicalConsultation";
import 'moment/locale/es';
import locale from 'antd/es/date-picker/locale/es_ES';
import moment from 'moment';
import debounce from 'lodash/debounce';
import { getCookie, listenerCookie } from "../../services/cookies";
import ConsultationTabs from "./components/ConsultationTabs";
import { handlers } from '../../helpers/helpers';
import { normalizeDocumentNumber } from "../../utils/inputValidations";
import { useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from "react-i18next";
import UnifiedSearchFilters from '../../components/UnifiedSearchFilters/UnifiedSearchFilters';
import settings from './countrySettings.json';
import config from "../../config";
import countrySettings from './countrySettings.json';
import { PRESTATIONS_SEARCH_DEBOUNCE_MS, isIncompleteSearchTerm } from '../../utils/searchConfig';

const { RangePicker } = DatePicker;
const { Option } = Select;
const defaultPagination = { pending: 1, reevaluation: 1, treated: 1, inProgress: 1 };

const medicalConsultationBaseFilters = ['company', 'outsourcing', 'thirdCompany', 'branchOffice', 'workerId', 'workerName', 'reservationNumber', 'protocol', 'dateRange', 'evaluationType', 'allBranchOffices'];
const medicalConsultationCustomFilters = ['consultationResult'];

const MedicalConsultationPage = () => {
  const urlSearchParams = new URLSearchParams(window.location.search);
  const location = useLocation()
  const navigate = useNavigate();
  const { t } = useTranslation();
  const countryCode = getCookie("country_code");

  const queryParams = Object.fromEntries(urlSearchParams.entries())

  const [queryFilters, setQueryFilters] = useState(Object.keys(queryParams).length !== 0 ? queryParams : {
    branch_office_id: getCookie('branch_office_id'),
    start_date: moment().format('YYYY-MM-DD'),
    end_date: moment().format('YYYY-MM-DD'),
    current_date: moment().format('YYYY-MM-DD'),
  });

  const [loading, setLoading] = useState({
    departments: false,
    pending: false,
    reevaluation: false,
    treated: false,
    inProgress: false
  });
  const [departments, setDepartments] = useState();
  const [doctors, setDoctors] = useState([]);
  const [services, setServices] = useState([]);
  const [branchOfficeId, setBranchOfficeId] = useState();
  const [isMedicalDirectorRol, setIsMedicalDirectorRol] = useState();
  const [isAuditorRol, setIsAuditorRol] = useState(false);
  const [pendingConsultations, setPendingConsultations] = useState();
  const [inProgressConsultations, setInProgressConsultations] = useState();
  const [reevaluationConsultations, setReevaluationConsultations] = useState();
  const [treatedConsultations, setTreatedConsultations] = useState();
  const [signSelectEnabled, setSignSelectEnabled] = useState(false);
  const [form] = Form.useForm();
  const [currentPage, setCurrentPage] = useState(defaultPagination);
  const [roles, setRoles] = useState(getCookie('roles'));

  const fetchDepartments = async () => {
    setLoading(loading => { return { ...loading, departments: true } });
    try {
      const response = await getDepartments();
      setDepartments(response);
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(loading => { return { ...loading, departments: false } });
    }
  };

  const fetchMedicalConsultations = useCallback(async (params) => {
    try {
      setLoading(loading => ({ ...loading, [params.state]: true }));
      if (params.query || params.department_id || params.doctor_id
        || (params.service_id && params.service_type) || (params.start_date && params.end_date) || params.signState
        || params.company_id || params.outsourcing_id || params.third_company_id || params.patient_document_number
        || params.patient_names || params.reservation_number || params.protocol_id || params.consultation_result) {
        const response = await consultationService.getConsultations(params);
        response.data = await Promise.all(response.data.map(async (exam) => {
          let unfinishedExams = undefined;
          if (exam.services && exam.services.find(service => service.exam_type === 'laboratory' || !service.exam_type)) {
            unfinishedExams = await consultationService.getUnfishedExams({
              admission_patient_id: exam.id,
              exam_type: 'laboratory',
            });
          }
          return ({
            ...exam,
            type: params.state,
            unfinishedExams,
          })
        }));

        if (params.state === 'pending') {
          setPendingConsultations(response);
        } else if (params.state === 'reevaluation') {
          setReevaluationConsultations(response);
        } else if (params.state === 'treated') {
          setTreatedConsultations(response);
        } else if (params.state === 'inProgress') {
          setInProgressConsultations(response);
        }
      } else {
        setPendingConsultations();
        setReevaluationConsultations();
        setTreatedConsultations();
        setInProgressConsultations();
      }
    } catch (error) {
      console.error(params.state, error);
      message.error({
        content: 'Error al obtener listado de consulta medica',
        style: {
          marginTop: "35vh",
          fontSize: 16,
        },
      });
    } finally {
      setLoading(loading => ({ ...loading, [params.state]: false }));
    }
  }, [branchOfficeId]);

  const initializeFormOnQueryParam = useCallback(async (params) => {
    let formParams = {};
    if (countryCode === 'PE') {
      formParams = {
        patient_document_number: params['patient_document_number'],
        patient_names: params['patient_names'],
        reservation_number: params['reservation_number'],
        protocol_id: params['protocol_id'] ? parseInt(params['protocol_id']) : undefined,
        consultation_result: params['consultation_result'] ? parseInt(params['consultation_result']) : undefined,
        evaluation_types: params['evaluation_types'] ? (Array.isArray(params['evaluation_types']) ? params['evaluation_types'] : [params['evaluation_types']]) : undefined,
        company_id: params['company_id'] ? parseInt(params['company_id']) : undefined,
        outsourcing_id: params['outsourcing_id'] ? parseInt(params['outsourcing_id']) : undefined,
        third_company_id: params['third_company_id'] ? parseInt(params['third_company_id']) : undefined,
        allBranchOffices: params['all_branch_offices'],
        departmentId: params['department_id'],
        doctorId: params['doctor_id'],
        serviceId: params['service_id'],
        serviceType: params['service_type'],
        signed: params['signState'],
        presignState: params['presign_state'],
        dates: params['start_date'] && params['end_date'] ? [moment(params.start_date), moment(params.end_date)] : [null, null]
      };
    } else {
      formParams = {
        searchInput: params['query'],
        allBranchOffices: params['all_branch_offices'],
        departmentId: params['department_id'],
        doctorId: params['doctor_id'],
        serviceId: params['service_id'],
        serviceType: params['service_type'],
        signed: params['signState'],
        presignState: params['presign_state'],
        dateRange: params['start_date'] && params['end_date'] ? [moment(params.start_date), moment(params.end_date)] : [null, null]
      };
    }
    form.setFieldsValue(formParams);
  }, [queryParams, queryFilters, countryCode, form]);

  useEffect(() => {
    fetchDepartments();
    fetchDoctors('');
    fetchServices('');
    setBranchOfficeId(getCookie('branch_office_id'));
    setIsMedicalDirectorRol(roles.includes('direccion_medica'));
    setIsAuditorRol(roles.includes('medical_auditor'));
  }, [branchOfficeId]);

  useEffect(() => {
    listenerCookie((data) => {
      if (data.name === 'branch_office_id') {
        setBranchOfficeId(data.value);
        const urlSearchParams = new URLSearchParams(window.location.search);
        const _filters = {
          ...Object.fromEntries(urlSearchParams.entries()),
          branch_office_id: data.value
        };
        setQueryFilters(_filters);
        handlers.replaceUrlQueryString(navigate, _filters);
      }
    })
  }, [])

  useEffect(() => {
    initializeFormOnQueryParam(queryFilters)

    fetchMedicalConsultations({
      page: currentPage.pending,
      state: 'pending',
      ...queryFilters
    });

    fetchMedicalConsultations({
      page: currentPage.reevaluation,
      state: 'reevaluation',
      ...queryFilters
    });

    fetchMedicalConsultations({
      page: currentPage.treated,
      state: 'treated',
      ...queryFilters
    });

    fetchMedicalConsultations({
      page: currentPage.inProgress,
      state: 'inProgress',
      ...queryFilters
    });
  }, [queryFilters, currentPage]);

  const fetchDoctors = debounce(async (name) => {
    try {
      const response = await getDoctors({
        ...(name && { query: name }),
        limit: 50
      });

      setDoctors(response);
      if (queryParams['doctor_id']) {
        form.setFieldsValue({
          doctorId: parseInt(queryParams['doctor_id'])
        })
      }
    } catch (error) {
      console.log(error);
    }
  }, 500);

  const fetchServices = debounce(async (name) => {
    if (isIncompleteSearchTerm(name)) return;
    const response = await getServices({
      ...(name && { query: name }),
      limit: 50
    });
    setServices(response);
    if (queryParams['service_id'] && queryParams['service_type']) {
      form.setFieldsValue({
        serviceId: parseInt(queryParams['service_id']),
        serviceType: queryParams['service_type']
      })
    }
  }, PRESTATIONS_SEARCH_DEBOUNCE_MS);

  const handleOnChangeSearch = (field, value, setter, option) => {
    if (value) {
      form.setFieldsValue({ [field]: value });
    } else {
      form.setFieldsValue({ [field]: null });
      if (field === "serviceId") {
        form.setFieldsValue({ serviceType: null });
      }
      setter([]);
    }
  };

  const onFinishFilters = (values) => {
    const homologation = {
      'serviceId': 'service_id',
      'serviceType': 'service_type',
      'departmentId': 'department_id',
      'allBranchOffices': 'all_branch_offices',
      'doctorId': 'doctor_id',
      'searchInput': 'query',
      'signed': 'signState',
      'presignState': 'presign_state',
      'company_id': 'company_id',
      'outsourcing_id': 'outsourcing_id',
      'third_company_id': 'third_company_id',
      'branch_office_id': 'branch_office_id',
      'patient_document_number': 'patient_document_number',
      'patient_names': 'patient_names',
      'reservation_number': 'reservation_number',
      'protocol_id': 'protocol_id',
      'consultation_result': 'consultation_result',
      'evaluation_types': 'evaluation_types',
      'start_date': 'start_date',
      'end_date': 'end_date'
    }

    const newFilters = {}

    if (values.dateRange && values.dateRange[0]) {
      newFilters['start_date'] = values['dateRange'][0].format('YYYY-MM-DD');
    }

    if (values.dateRange && values.dateRange[1]) {
      newFilters['end_date'] = values['dateRange'][1].format('YYYY-MM-DD');
    }

    if (values.dates && values.dates[0]) {
      newFilters['start_date'] = values['dates'][0].format('YYYY-MM-DD');
      newFilters['end_date'] = values['dates'][1].format('YYYY-MM-DD');
    }

    Object.keys(values).forEach(key => {
      if (![undefined, null, 0].includes(values[key]) && key !== 'dateRange' && key !== 'dates') {
        if (key === 'searchInput') {
          newFilters[homologation[key]] = normalizeDocumentNumber(values[key]);
        } else {
          newFilters[homologation[key]] = values[key];
        }
      }
    });

    if (newFilters['all_branch_offices'] === true) {
      delete newFilters['branch_office_id'];
    } else {
      delete newFilters['all_branch_offices'];
      if (!newFilters['branch_office_id']) {
        newFilters['branch_office_id'] = branchOfficeId;
      }
    }
    handlers.replaceUrlQueryString(navigate, newFilters);
    setQueryFilters(newFilters);
    setCurrentPage(defaultPagination);
  };


  const removeFilters = () => {
    form.resetFields();
    setQueryFilters({
      branch_office_id: branchOfficeId
    });
    setCurrentPage(defaultPagination);
    const uri = location.pathname;
    navigate(`${uri}`)
  };

  const isAuditSignRequiredByCountry = () => {
    return settings.medicalConsultation.auditorSignature[countryCode];
  }

  const isAuditSignRequiredByRol = () => {
    return isAuditorRol;
  }

  const isAuditSignRequiredByCompany = (exam) => {
    return (config?.restrictedCompaniesSignIds?.split(',') || [])?.includes(exam?.company_id?.toString());
  }

  const alignment = {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center'
  };

  const auditSignActiveByCountry = isAuditSignRequiredByCountry();
  const auditSignActiveByRol = isAuditSignRequiredByRol();
  const auditSignActiveByCompany = inProgressConsultations?.data?.some(exam => isAuditSignRequiredByCompany(exam)) || treatedConsultations?.data?.some(exam => isAuditSignRequiredByCompany(exam));

  useEffect(() => {
    if (auditSignActiveByCompany) {
      setSignSelectEnabled(true);
    }
  }, [inProgressConsultations, treatedConsultations]);

  return (
    <Row justify="center" className="medical-consultation-page">
      <Col lg={24} xs={24}>
        <Row justify="center" gutter={[0, 10]}>
          <Col lg={22} xs={22} className="form-wrapper">
            <h1 className="mb-20 mt-15">{t('medicalConsultations.byFilters.byFilters')}</h1>
            {countryCode === 'PE' ? (
              <UnifiedSearchFilters
                form={form}
                moduleName={t('medicalConsultations.byFilters.byFilters')}
                baseFilters={medicalConsultationBaseFilters}
                customFilters={medicalConsultationCustomFilters}
                isCollapsible={true}
                userHasSystemWideAccess={isMedicalDirectorRol}
                onSearch={onFinishFilters}
                onClear={removeFilters}
                loading={loading.pending || loading.reevaluation || loading.treated || loading.inProgress}
                initialValues={{
                  dates: [moment(queryFilters.start_date), moment(queryFilters.end_date)],
                  departmentId: queryFilters.department_id,
                  doctorId: queryFilters.doctor_id,
                  serviceId: queryFilters.service_id,
                  serviceType: queryFilters.service_type,
                  branch_office_id: queryFilters.branch_office_id ? parseInt(queryFilters.branch_office_id) : parseInt(getCookie('branch_office_id')),
                  company_id: queryFilters.company_id ? parseInt(queryFilters.company_id) : undefined,
                  outsourcing_id: queryFilters.outsourcing_id ? parseInt(queryFilters.outsourcing_id) : undefined,
                  third_company_id: queryFilters.third_company_id ? parseInt(queryFilters.third_company_id) : undefined,
                  patient_document_number: queryFilters.patient_document_number,
                  patient_names: queryFilters.patient_names,
                  reservation_number: queryFilters.reservation_number,
                  protocol_id: queryFilters.protocol_id ? parseInt(queryFilters.protocol_id) : undefined,
                  consultation_result: queryFilters.consultation_result ? parseInt(queryFilters.consultation_result) : undefined,
                  evaluation_types: queryFilters.evaluation_types ? (Array.isArray(queryFilters.evaluation_types) ? queryFilters.evaluation_types : [queryFilters.evaluation_types]) : undefined,
                  signed: queryFilters.signState,
                  presignState: queryFilters.presign_state,
                  allBranchOffices: queryFilters.all_branch_offices === 'true'
                }}
                customNodes={[
                  <Col xl={6} lg={8} md={12} xs={24}>
                    {loading.departments ? (
                      <Spin className="custom-spinner mt-10" style={alignment} />
                    ) : departments && (
                      <Form.Item name="departmentId" label={t('medicalConsultations.byFilters.departmentLabel')}>
                        <Select className="cmt-select" placeholder={t('medicalConsultations.byFilters.departmentPlaceholder')}>
                          <Option key={0} value={0}>Todos</Option>
                          {departments.map((item, index) => (
                            <Option key={index + 1} value={item.id}>{item.name}</Option>
                          ))}
                        </Select>
                      </Form.Item>
                    )}
                  </Col>,
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Form.Item name="doctorId" label={t('medicalConsultations.byFilters.doctorLabel')}>
                      <Select
                        className="cmt-select"
                        placeholder={t('medicalConsultations.byFilters.doctorPlaceholder')}
                        showSearch
                        showArrow={false}
                        filterOption={false}
                        allowClear
                        onSearch={fetchDoctors}
                        onChange={(value) => handleOnChangeSearch('doctorId', value, setDoctors)}
                        onClear={(value) => handleOnChangeSearch('doctorId', value, setDoctors)}
                        notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>No hay coincidencias</div>} />}
                      >
                        {doctors && doctors.map((item, index) => (
                          <Option key={index} value={item.id}>{item.fullname}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>,
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Form.Item name="serviceType" hidden><Input /></Form.Item>
                    <Form.Item name="serviceId" label={t('medicalConsultations.byFilters.serviceLabel')}>
                      <Select
                        className="cmt-select"
                        placeholder={t('medicalConsultations.byFilters.servicePlaceholder')}
                        showSearch
                        showArrow={false}
                        filterOption={false}
                        allowClear
                        onSearch={fetchServices}
                        onChange={(value, option) => {
                          if (option) form.setFieldsValue({ 'serviceType': option.type });
                        }}
                        onClear={(value) => handleOnChangeSearch('serviceId', value, setServices)}
                        notFoundContent={<Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description={<div>No hay coincidencias</div>} />}
                      >
                        {services?.data && services.data.map((item, index) => (
                          <Option key={index} value={item.id} type={item.type}>{item.name}</Option>
                        ))}
                      </Select>
                    </Form.Item>
                  </Col>,
                  <Col xl={6} lg={8} md={12} xs={24}>
                    <Popover
                      content={
                        !auditSignActiveByRol ? t('medicalConsultations.byFilters.signStatus.signStatusPopoverContentRol') :
                          !auditSignActiveByCompany ? t('medicalConsultations.byFilters.signStatus.signStatusPopoverContentCompany') : ''
                      }
                      trigger={auditSignActiveByRol && (auditSignActiveByCountry || auditSignActiveByCompany || signSelectEnabled) ? '' : 'hover'}
                      overlayStyle={{ maxWidth: 400 }}
                      placement="bottom"
                    >
                      <Form.Item name="signed" label={t('medicalConsultations.byFilters.signStatus.signStatusLabel')} className="mb-0">
                        <Select
                          className="cmt-select"
                          placeholder={t('medicalConsultations.byFilters.signStatus.signStatusPlaceholder')}
                          disabled={!(auditSignActiveByRol && (auditSignActiveByCountry || auditSignActiveByCompany || signSelectEnabled))}
                        >
                          <Option key={0} value="all">{t('medicalConsultations.byFilters.signStatus.signStatusPlaceholder')}</Option>
                          <Option key={1} value="notEvaluated">{t('medicalConsultations.byFilters.signStatus.notEvaluated')}</Option>
                          <Option key={2} value="unsigned">{t('medicalConsultations.byFilters.signStatus.unsigned')}</Option>
                          <Option key={3} value="signed">{t('medicalConsultations.byFilters.signStatus.signed')}</Option>
                        </Select>
                      </Form.Item>
                    </Popover>
                  </Col>,
                  ...(countrySettings.medicalConsultation.allowPresign[countryCode] ? [
                    <Col xl={6} lg={8} md={12} xs={24}>
                      <Form.Item name="presignState" label={t('medicalConsultations.byFilters.presignStatus.presignStatusLabel')} className="mb-0">
                        <Select className="cmt-select" placeholder={t('medicalConsultations.byFilters.presignStatus.presignStatusPlaceholder')}>
                          <Option key={0} value={0}>{t('medicalConsultations.byFilters.presignStatus.presignStatusPlaceholder')}</Option>
                          <Option key={2} value="failed_sign">{t('medicalConsultations.byFilters.presignStatus.failed')}</Option>
                          <Option key={3} value="signed">{t('medicalConsultations.byFilters.presignStatus.signed')}</Option>
                        </Select>
                      </Form.Item>
                    </Col>
                  ] : [])
                ]}
              />
            ) : (
              <Form
                form={form}
                onFinish={onFinishFilters}
                size="large"
                layout="vertical"
              >
                <Row>

                  <Col lg={24} xs={24}>
                    <Row justify="space-between" gutter={15}>
                      <Col lg={6} xs={24}>
                        <Form.Item name="dateRange" label="Fecha">
                          <RangePicker
                            locale={locale}
                            format="DD-MM-YYYY"
                            inputReadOnly
                            placeholder={['Desde', 'Hasta']}
                            style={{ width: '100%' }}
                            id="rangePicker"
                          />
                        </Form.Item>
                      </Col>
                      <Col lg={6} xs={24}>
                        {
                          loading.departments ?
                            <Spin className="custom-spinner mt-10" style={alignment} />
                            :
                            departments &&
                            <Form.Item
                              name="departmentId"
                              label={t('medicalConsultations.byFilters.departmentLabel')}
                            >
                              <Select
                                className="cmt-select"
                                placeholder={t('medicalConsultations.byFilters.departmentPlaceholder')}
                                id="departmentSelect"
                              >
                                <Option key={0} value={0}>
                                  Todos
                                </Option>
                                {
                                  departments.map((item, index) => {
                                    return (
                                      <Option key={index + 1} value={item.id}>
                                        {item.name}
                                      </Option>
                                    )
                                  })
                                }
                              </Select>
                            </Form.Item>
                        }
                      </Col>
                      <Col lg={6} xs={24}>
                        <Form.Item
                          name="doctorId"
                          label={t('medicalConsultations.byFilters.doctorLabel')}
                        >
                          <Select
                            className="cmt-select"
                            id="doctorSelect"
                            placeholder={t('medicalConsultations.byFilters.doctorPlaceholder')}
                            showSearch
                            showArrow={false}
                            filterOption={false}
                            allowClear
                            onSearch={fetchDoctors}
                            onChange={
                              (value) => handleOnChangeSearch('doctorId', value, setDoctors)
                            }
                            onClear={
                              (value) => handleOnChangeSearch('doctorId', value, setDoctors)
                            }
                            notFoundContent={
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                  <div>
                                    No hay coincidencias
                                  </div>
                                }
                              />
                            }
                          >
                            {
                              doctors && doctors.map((item, index) => {
                                return (
                                  <Option key={index} value={item.id}>
                                    {item.fullname}
                                  </Option>
                                )
                              })
                            }
                          </Select>
                        </Form.Item>
                      </Col>
                      <Col lg={6} xs={24}>
                        <Form.Item name="serviceType" hidden>
                          <Input />
                        </Form.Item>
                        <Form.Item
                          name="serviceId"
                          label={t('medicalConsultations.byFilters.serviceLabel')}
                        >
                          <Select
                            className="cmt-select"
                            id="serviceInput"
                            placeholder={t('medicalConsultations.byFilters.servicePlaceholder')}
                            showSearch
                            showArrow={false}
                            filterOption={false}
                            allowClear
                            style={{ width: '100%', color: '#505050' }}
                            onSearch={fetchServices}
                            onChange={
                              (value, option) => {
                                if (option) {
                                  form.setFieldsValue({
                                    'serviceType': option.type
                                  })
                                }
                              }
                            }
                            onClear={
                              (value) => handleOnChangeSearch('serviceId', value, setServices)
                            }
                            notFoundContent={
                              <Empty
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                                description={
                                  <div>
                                    No hay coincidencias
                                  </div>
                                }
                              />
                            }
                          >
                            {
                              (services && services.data) && services.data.map((item, index) => {
                                return (
                                  <Option key={index} value={item.id} type={item.type}>
                                    {item.name}
                                  </Option>
                                )
                              })
                            }
                          </Select>
                        </Form.Item>
                      </Col>
                    </Row>
                  </Col>
                  <Col lg={24} xs={24}>
                    <Row justify="space-between">
                      <Col lg={24} xs={24}>
                        <Row gutter={15} align="bottom">
                          <Col lg={6} xs={24}>
                            <Popover
                              content={
                                !auditSignActiveByRol
                                  ? t('medicalConsultations.byFilters.signStatus.signStatusPopoverContentRol')
                                  : !auditSignActiveByCompany
                                    ? t('medicalConsultations.byFilters.signStatus.signStatusPopoverContentCompany')
                                    : ''
                              }
                              trigger={auditSignActiveByRol && (auditSignActiveByCountry || auditSignActiveByCompany || signSelectEnabled) ? '' : 'hover'}
                              overlayStyle={{ maxWidth: 400 }}
                              placement="bottom"
                            >
                              <Form.Item
                                name="signed"
                                label={t('medicalConsultations.byFilters.signStatus.signStatusLabel')}
                                className="mb-0"
                              >

                                <Select
                                  className="cmt-select"
                                  placeholder={t('medicalConsultations.byFilters.signStatus.signStatusPlaceholder')}
                                  id="signSelect"
                                  disabled={!(auditSignActiveByRol && (auditSignActiveByCountry || auditSignActiveByCompany || signSelectEnabled))}
                                >
                                  <Option key={0} value="all">
                                    {t('medicalConsultations.byFilters.signStatus.signStatusPlaceholder')}
                                  </Option>
                                  <Option key={1} value="notEvaluated">
                                    {t('medicalConsultations.byFilters.signStatus.notEvaluated')}
                                  </Option>
                                  <Option key={2} value="unsigned">
                                    {t('medicalConsultations.byFilters.signStatus.unsigned')}
                                  </Option>
                                  <Option key={3} value="signed">
                                    {t('medicalConsultations.byFilters.signStatus.signed')}
                                  </Option>
                                </Select>
                              </Form.Item>
                            </Popover>
                          </Col>
                          {countrySettings.medicalConsultation.allowPresign[countryCode] &&
                            <Col lg={6} xs={24}>
                              <Form.Item
                                name="presignState"
                                label={t('medicalConsultations.byFilters.presignStatus.presignStatusLabel')}
                                className="mb-0">
                                <Select
                                  className="cmt-select"
                                  placeholder={t('medicalConsultations.byFilters.presignStatus.presignStatusPlaceholder')}
                                  id="presignSelect"
                                >
                                  <Option key={0} value={0}>
                                    {t('medicalConsultations.byFilters.presignStatus.presignStatusPlaceholder')}
                                  </Option>
                                  {/*
                                  <Option key={1} value="waitingLab">
                                    {t('medicalConsultations.byFilters.presignStatus.waitingLab')}
                                  </Option>
                                  */ }
                                  <Option key={2} value="failed_sign">
                                    {t('medicalConsultations.byFilters.presignStatus.failed')}
                                  </Option>
                                  <Option key={3} value="signed">
                                    {t('medicalConsultations.byFilters.presignStatus.signed')}
                                  </Option>
                                </Select>
                              </Form.Item>
                            </Col>}

                          <Col lg={countrySettings.medicalConsultation.allowPresign[countryCode] ? 6 : 12} xs={24}>
                            <Form.Item name="searchInput" className="mb-0 mt-15">
                              <Input
                                id="inputSearch"
                                className="cmt-input"
                                placeholder={t('medicalConsultations.byFilters.searchInputPlaceholder')}
                                prefix={<i className="icon-system-search" />}
                              />
                            </Form.Item>
                          </Col>
                          <Col style={alignment} className="mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30">
                            <Button
                              id="searchButton"
                              htmlType="submit"
                              className="cmt-button primary"
                              type="primary"
                            >
                              Buscar
                            </Button>
                          </Col>
                          <Col style={alignment} className="mt-lg-0 mt-md-30 mt-sm-30 mt-xs-30">
                            <Button
                              onClick={removeFilters}
                              className="cmt-button primary non-outline p-0"
                              type="primary"
                            >
                              Borrar filtros
                            </Button>
                          </Col>
                        </Row>
                      </Col>
                      {
                        isMedicalDirectorRol && (
                          <Col lg={6} xs={24} style={alignment} className="mt-lg-0 mt-md-20 mt-sm-20 mt-xs-20">
                            <Row justify="center" align="center">
                              <Col>
                                <Form.Item name="allBranchOffices" valuePropName="checked">
                                  <Checkbox className="cmt-checkbox" />
                                </Form.Item>
                              </Col>
                              <Col>
                                <p className="ml-10 mt-10">{t('medicalConsultations.byFilters.showAllBranchOffices')}</p>
                              </Col>
                            </Row>
                          </Col>
                        )
                      }
                    </Row>
                  </Col>
                </Row>
              </Form>
            )}
          </Col>

          <Col lg={24} xs={24} className="tabs-wrapper">
            {
              form &&
              <ConsultationTabs
                form={form}
                pendingConsultations={pendingConsultations}
                reevaluationConsultations={reevaluationConsultations}
                treatedConsultations={treatedConsultations}
                inProgressConsultations={inProgressConsultations}
                loading={loading}
                currentPage={currentPage}
                setCurrentPage={setCurrentPage}
              />
            }
          </Col>
        </Row>
      </Col>
    </Row>
  );
};

export default MedicalConsultationPage;
